import React, { useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Button } from 'react-aria-components';
import { httpGet } from '../../../modules/Backend';
import { getCurrentFormattedDate } from '../../../helpers/DateHelper';
import LoadingButton from '../LoadingButton';
import BarCodeGenerator from '../../lib/inputs/BarCodeGenerator';
import Modal from '../layout/Modal';

interface PrintableComponentProps {
  data: any;
  ref: React.RefObject<HTMLDivElement>;
}

const PrintableComponent = React.forwardRef<HTMLDivElement, PrintableComponentProps>(({ data }, ref) => (
  <div ref={ref}>
    <style>{`
      @page { size: landscape; }
      @media print {
        [data-print-ref-row] { margin-top: -10px; margin-left: 0; }
        [data-print-data] { margin-left: 7px; }
        [data-print-box-type] { margin-right: 7px; margin-left: 0; }
      }
    `}</style>
    {data && (
      <div
        id="print"
        style={{
          minHeight: 125,
          width: 403,
          flexShrink: 0,
          marginTop: 0,
          marginBottom: 15,
          padding: '4px 0',
        }}
      >
        <div style={{ marginTop: 0, marginBottom: 14 }}>
          <BarCodeGenerator input={data.reference} barCodeType="code128" />
        </div>
        <div
          data-print-data
          style={{ marginLeft: 20, marginTop: 4 }}
        >
          <div
            data-print-ref-row
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              marginTop: 6
            }}
          >
            <label style={{ fontSize: '12pt', width: 'max-content', fontWeight: 800 }}>{data.reference}</label>
            <label
              data-print-box-type
              style={{ fontSize: '12pt', width: 'max-content', textAlign: 'right', flexShrink: 0, fontWeight: 800 }}
            >
              {data.boxType ?? ''}
            </label>
          </div>
          <div style={{ marginTop: 4 }}>
            <label style={{ fontSize: '6.2pt', fontWeight: 800 }}>
              {data.owner != null && data.owner !== '' ? `Belongs To: ${data.owner}` : ''}
            </label>
          </div>
          <div style={{ marginTop: 4 }}>
            <label style={{ fontSize: '6.2pt', fontWeight: 800 }}>Packed By: {data.packedBy}</label>
          </div>
          <div style={{ marginTop: 4 }}>
            <label style={{ fontSize: '6.2pt', fontWeight: 800 }}>Packed On: {getCurrentFormattedDate()}</label>
          </div>
        </div>
      </div>
    )}
  </div>
));

interface PrinterProps {
  outerCubeId: string;
}

function PrintOuterCubeLabel({ outerCubeId }: PrinterProps) {
  const [data, setData] = useState<any>();
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await httpGet(`api/outerCubes/label?id=${outerCubeId}`);
      if (response.data) {
        setData(response.data);
        setModalOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => setPrinting(false),
  });

  const onPrintClick = () => {
    if (!componentRef.current) return;
    setPrinting(true);
    handlePrint();
  };

  const closeModal = () => {
    setModalOpen(false);
    setData(undefined);
  };

  return (
    <div>
      <LoadingButton
        className="button button--secondary button--with-icon button--small"
        onPress={fetchData}
        isDisabled={loading}
        isLoading={loading}
        loadingText="Preparing"
        buttonText="Label"
      >
        <></>
      </LoadingButton>
      <Modal isOpen={modalOpen} modalClosed={closeModal}>
        <div className="display--flex flex__direction--column alignItems--center gap--s">
          <div style={{ flexShrink: 0, minWidth: 403 }}>
            {data && <PrintableComponent ref={componentRef} data={data} />}
          </div>
          <div className="display--flex gap--m margin__top--xxs">
            <Button
              className="button button--large button--secondary button--with-icon"
              onPress={closeModal}
              isDisabled={printing}
            >
              <p className="button-text">Close</p>
            </Button>
            <LoadingButton
              className="button button--large button--primary button--with-icon"
              onPress={onPrintClick}
              isDisabled={!data || printing}
              isLoading={printing}
              loadingText="Printing"
              buttonText="Print"
            >
              <></>
            </LoadingButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default PrintOuterCubeLabel;
